/**
 * @fileoverview Background submission processor store.
 *
 * Manages a local FIFO queue for generation submissions, decoupling UI interactions
 * from network operations. Submissions appear as placeholder nodes on the canvas
 * immediately and are processed in the background.
 *
 * Key design decisions:
 * - Local-only (Zustand, not Yjs): Other collaborators shouldn't see submissions until server-acknowledged
 * - FIFO with dependency tracking: Predictable ordering, supports batch with root→variations
 * - Jobs added to Yjs only on success: Prevents orphaned entries
 */

import { create } from 'zustand'
import { v4 as uuid } from 'uuid'
import type { AssetOperation, GenerationParams, Job } from '@/types'
import { captureSentryError } from '@/lib/sentry'
import { useInsufficientCreditsStore } from '@/store/insufficientCreditsStore'
import { useGenerationBlockedStore } from '@/store/generationBlockedStore'
import { useStore } from '@/store/yjsStore'

/**
 * Coded 4xx refusals the server makes deliberately, whose `error` field is copy
 * written for the user (an org admin disabled the model, BYOK isn't set up).
 * They get the blocked dialog and stay out of Sentry — they are decisions, not
 * defects. Billing refusals are handled by status instead; see `isBillingRefusal`.
 */
const INTENTIONAL_REFUSAL_CODES = new Set([
  'MODEL_DISABLED_BY_ORG',
  'RESOLUTION_DISABLED_BY_ORG',
  'BYOK_MISCONFIGURED',
  'BYOK_REQUIRED',
])

// =============================================================================
// Types
// =============================================================================

/**
 * Local submission status (not in Yjs).
 * - queued: Waiting in FIFO queue
 * - processing: Currently uploading/POSTing
 * - completed: Successfully submitted (transient, removed immediately)
 * - failed: Pre-upload or POST failed
 */
export type SubmissionStatus = 'queued' | 'processing' | 'completed' | 'failed'

/**
 * Error classification for submission failures.
 */
export interface SubmissionError {
  code: string
  message: string
  retryable: boolean
  requiredCost?: number
  currentBalance?: number
  /** Billing mode of the org that was short (e.g. 'team_tab'). Undefined = personal. */
  billingMode?: string
}

/**
 * Parameters needed to execute a submission.
 * Mirrors the generateSingleShot params from VideoGenerationPanel.
 */
export interface SubmissionParams {
  projectId: string
  sceneId: string
  prompt: string
  generationParams: GenerationParams
  imageUrl?: string
  /** Inpainting mask URL (pending:<uuid> marker; resolved before submission). */
  maskUrl?: string
  hasStartFrame: boolean
  panelMode: 'generate' | 'extend' | 'edit'
  extensionSourceFrame?: number
  sourceAssetId?: string
  // Pre-allocated hierarchy values
  preallocatedParentId: string | null
  preallocatedOperation: AssetOperation
  preallocatedSiblingIndex: number
  preallocatedShotNumber: number
  preallocatedVariationNumber: number
  preallocatedOffshootNumber?: number
  preallocatedBinId?: string
  preallocatedAspectRatio?: string
  // Frame source positions
  startFrameSourcePosition?: number
  endFrameSourcePosition?: number
  // Frame references for full-res extraction
  startFrameRef?: { assetId: string; timestamp: number; storageAssetId?: string }
  endFrameRef?: { assetId: string; timestamp: number; storageAssetId?: string }
  // Timing estimate
  estimatedTotalSeconds?: number
  // Canvas position
  preallocatedPosition?: { x: number; y: number }
  // Pre-uploaded frame URLs (populated after preUploadFrames completes)
  uploadedFrameUrls?: {
    imageUrl?: string
    endFrameUrl?: string
    middleFrameUrl?: string
    maskUrl?: string
    imageReferences?: string[]
    elements?: { frontalImageUrl: string; referenceImageUrls?: string[] }[]
  }
  // Model ID for analytics
  modelId: string
  // Draft node associated with this submission for collaborator-visible generating state
  draftAssetId?: string
  // Draft node to remove on success (deferred cleanup to avoid losing the draft on transient failures)
  draftAssetIdToRemove?: string
}

/**
 * A queued submission in the local processor.
 */
export interface QueuedSubmission {
  /** Unique submission ID (UUID) */
  id: string
  /** All parameters needed for submission */
  params: SubmissionParams
  /** Optional: wait for this submissionId to complete first */
  dependsOn?: string
  /** Current status */
  status: SubmissionStatus
  /** Populated on success: the server-assigned jobId */
  resultJobId?: string
  /** Error details if status is 'failed' */
  error?: SubmissionError
  /** Timestamp when submission was created */
  createdAt: Date
  /** Timestamp when submission failed (for auto-dismiss) */
  failedAt?: Date
  /**
   * Parent canvas position captured at enqueue time. Captured here (rather than
   * in onSuccess) so it reflects the same parent position that the panel used
   * when computing `params.preallocatedPosition`, even if the parent is dragged
   * during the API-call window.
   */
  parentPositionAtSubmission?: { x: number; y: number }
}

/**
 * Placeholder node data for canvas rendering.
 * Matches Job shape enough for canvas nodes to render.
 */
/**
 * A submission rejected by our own client-side pipeline before any network
 * call — bad or not-yet-ready input, not connectivity. Thrown with a message
 * written for the user; the processor must surface it verbatim instead of
 * rewriting it to "Connection failed", which sends people to check their
 * wifi for an input problem.
 */
export class SubmissionInputError extends Error {
  readonly isSubmissionInputError = true
  constructor(
    message: string,
    readonly retryable: boolean = true,
  ) {
    super(message)
    this.name = 'SubmissionInputError'
  }
}

export interface SubmissionPlaceholder {
  /** Submission ID (used as node ID: `submission-${id}`) */
  id: string
  /** Status for canvas rendering */
  status: SubmissionStatus
  /** Error if failed */
  error?: SubmissionError
  /** Prompt for display */
  prompt: string
  /** Scene this submission belongs to */
  sceneId: string
  /** Hierarchy fields for canvas positioning */
  parentId: string | null
  operation: AssetOperation
  siblingIndex: number
  shotNumber: number
  variationNumber: number
  offshootNumber?: number
  binId?: string
  /** Canvas position */
  position?: { x: number; y: number }
  /** When the submission was created */
  createdAt: string
  /** Project ID */
  projectId: string
  /** Draft node already representing this submission on canvas, if any */
  draftAssetId?: string
  /** Duration in ticks (for node display) */
  duration: number
  /** Aspect ratio for placeholder sizing */
  aspectRatio?: string
}

// =============================================================================
// Constants
// =============================================================================

/** Rate limiting: soft warning threshold */
const SOFT_LIMIT_COUNT = 10
/** Rate limiting: hard rejection threshold */
const HARD_LIMIT_COUNT = 20
/** Rate limiting: time window in milliseconds */
const RATE_LIMIT_WINDOW_MS = 60_000 // 1 minute
/** Auto-dismiss failed submissions after this duration */
const AUTO_DISMISS_MS = 5 * 60 * 1000 // 5 minutes

// =============================================================================
// Store
// =============================================================================

interface SubmissionProcessorState {
  /** FIFO queue of submissions */
  submissions: Map<string, QueuedSubmission>
  /** Whether the processor is currently running */
  isProcessing: boolean
  /** Timestamps of recent submissions for rate limiting */
  recentSubmissionTimestamps: Date[]
  /** Whether to show the first generation congrats modal */
  showFirstGenerationModal: boolean
  /** Context for the first generation (for "create a variation" action) */
  firstGenerationContext: { jobId: string; sceneId: string; operation?: string } | null
  /** Whether the first generation has completed (triggers "completed" phase of modal) */
  firstGenerationCompleted: boolean
  /** Whether to show the onboarding Seedance generation modal */
  showOnboardingAnimaticModal: boolean
  /** Context for the onboarding Seedance generation modal */
  onboardingAnimaticContext: { jobId: string; assetId: string } | null
  /** Node ID to focus and open context menu on (set by FirstGenerationModal) */
  pendingContextMenuNodeId: string | null

  // Actions
  /** Enqueue a new submission (returns submissionId) */
  enqueue: (params: SubmissionParams, dependsOn?: string) => string
  /** Enqueue multiple submissions as a batch (returns submissionIds) */
  enqueueBatch: (batch: Array<{ params: SubmissionParams; dependsOn?: string }>) => string[]
  /** Retry a failed submission (re-enqueues at back) */
  retry: (submissionId: string) => void
  /** Dismiss a submission (remove from queue) */
  dismiss: (submissionId: string) => void
  /** Update submission status (internal) */
  updateSubmission: (
    submissionId: string,
    updates: Partial<Pick<QueuedSubmission, 'status' | 'resultJobId' | 'error' | 'failedAt' | 'params'>>,
  ) => void
  /** Get submission by ID */
  getSubmission: (submissionId: string) => QueuedSubmission | undefined
  /** Get all submissions for a scene (for canvas rendering) */
  getSubmissionsForScene: (sceneId: string) => QueuedSubmission[]
  /** Convert submissions to placeholders for canvas */
  getPlaceholdersForScene: (sceneId: string) => SubmissionPlaceholder[]
  /** Clear all submissions (for project switch) */
  clear: () => void
  /** Get current queue depth */
  getQueueDepth: () => number
  /** Check if rate limited (returns { limited: boolean, softWarning: boolean }) */
  checkRateLimit: () => { limited: boolean; softWarning: boolean }
  /** Start the processor (called once on mount) */
  startProcessor: (
    processSubmission: (
      submission: QueuedSubmission,
    ) => Promise<{ jobId: string; assetNumber?: number; isFirstGeneration?: boolean }>,
    onSuccess: (submission: QueuedSubmission, jobId: string, assetNumber?: number, isFirstGeneration?: boolean) => void,
  ) => void
  /** Stop the processor */
  stopProcessor: () => void
  /** Update a submission's canvas position (called during drag) */
  updateSubmissionPosition: (submissionId: string, position: { x: number; y: number }) => void
  /** Mark processor as not running (for cleanup) */
  setProcessing: (isProcessing: boolean) => void
  /** Dismiss the first generation congrats modal */
  dismissFirstGenerationModal: () => void
  /** Mark the first generation as completed (show timeline tutorial phase) */
  setFirstGenerationCompleted: () => void
  /** Check if a completed job is the first video generation and transition modal accordingly */
  checkFirstGenerationCompleted: (completedJobId: string) => void
  /** Show the onboarding Seedance generation modal after the house-funded fire starts */
  showOnboardingAnimaticModalForJob: (context: { jobId: string; assetId: string }) => void
  /** Dismiss the onboarding Seedance generation modal */
  dismissOnboardingAnimaticModal: () => void
  /** Set pending context menu node (focus + open context menu) */
  setPendingContextMenuNodeId: (nodeId: string | null) => void
}

/**
 * Convert a QueuedSubmission to a SubmissionPlaceholder for canvas rendering.
 */
function submissionToPlaceholder(submission: QueuedSubmission): SubmissionPlaceholder {
  const { params } = submission
  return {
    id: submission.id,
    status: submission.status,
    error: submission.error,
    prompt: params.prompt,
    sceneId: params.sceneId,
    parentId: params.preallocatedParentId,
    operation: params.preallocatedOperation,
    siblingIndex: params.preallocatedSiblingIndex,
    shotNumber: params.preallocatedShotNumber,
    variationNumber: params.preallocatedVariationNumber,
    offshootNumber: params.preallocatedOffshootNumber,
    binId: params.preallocatedBinId,
    position: params.preallocatedPosition,
    createdAt: submission.createdAt.toISOString(),
    projectId: params.projectId,
    draftAssetId: params.draftAssetId ?? params.draftAssetIdToRemove,
    duration: params.generationParams.duration * 48000, // seconds to ticks
    aspectRatio: params.preallocatedAspectRatio ?? params.generationParams.aspectRatio,
  }
}

/**
 * Read the current canvas position of the parent at enqueue time so it stays
 * aligned with `params.preallocatedPosition` (which the panel computed in the
 * same synchronous tick). Falls back to the parent's job position if the parent
 * itself hasn't completed yet.
 */
function captureParentPositionAtSubmission(parentId: string | null): { x: number; y: number } | undefined {
  if (!parentId) return undefined
  try {
    const positions = useStore.getState().canvasPositions
    const pos = positions.get(`asset-${parentId}`) ?? positions.get(`job-${parentId}`)
    return pos ? { x: pos.x, y: pos.y } : undefined
  } catch {
    return undefined
  }
}

export const useSubmissionProcessorStore = create<SubmissionProcessorState>((set, get) => {
  const getTrackedDraftAssetId = (params: SubmissionParams): string | undefined => {
    return params.draftAssetId ?? params.draftAssetIdToRemove
  }

  const setDraftGeneratingState = (draftAssetId: string, isGenerating: boolean) => {
    try {
      const yjsState = useStore.getState()
      if (!yjsState.assets.has(draftAssetId)) return
      yjsState.setDraftSettings(draftAssetId, {
        generatingStartedAt: isGenerating ? Date.now() : null,
        // A fresh submission supersedes the previous attempt's failure chip.
        ...(isGenerating ? { generationFailure: null } : {}),
      })
    } catch (error) {
      // best-effort; draft sync should never block queue transitions
      captureSentryError(error, { operation: 'setDraftGeneratingState' })
    }
  }

  /**
   * Persist why a submission failed onto its draft node. The failed
   * QueuedSubmission is per-tab and auto-dismisses, and the Pixi canvas draws
   * nothing for submission placeholders — without this write a submit-time
   * failure reverts the draft's generating overlay and shows nothing at all.
   * The draft preview layer renders it; cleared by the next submission.
   */
  const setDraftGenerationFailure = (draftAssetId: string, failure: { code?: string; message: string }) => {
    try {
      const yjsState = useStore.getState()
      if (!yjsState.assets.has(draftAssetId)) return
      yjsState.setDraftSettings(draftAssetId, {
        generationFailure: { ...failure, failedAt: Date.now() },
      })
    } catch (error) {
      // best-effort; failure display should never block queue transitions
      captureSentryError(error, { operation: 'setDraftGenerationFailure' })
    }
  }

  const hasActiveSubmissionForDraft = (draftAssetId: string): boolean => {
    return Array.from(get().submissions.values()).some(submission => {
      return (
        getTrackedDraftAssetId(submission.params) === draftAssetId &&
        (submission.status === 'queued' || submission.status === 'processing')
      )
    })
  }

  const syncDraftGeneratingState = (draftAssetId: string) => {
    setDraftGeneratingState(draftAssetId, hasActiveSubmissionForDraft(draftAssetId))
  }

  /**
   * Record a submission failure on its draft — unless another submission for
   * the same draft is still active. Writing unconditionally would restore the
   * older attempt's error right after `setDraftGeneratingState(true)` cleared
   * it, leaving a stale failure in collaborative state once the newer
   * submission's generating lock expires.
   */
  const recordDraftFailureIfIdle = (draftAssetId: string, failure: { code?: string; message: string }) => {
    const stillGenerating = hasActiveSubmissionForDraft(draftAssetId)
    setDraftGeneratingState(draftAssetId, stillGenerating)
    if (!stillGenerating) setDraftGenerationFailure(draftAssetId, failure)
  }

  // Processor loop reference
  let processorRunning = false
  let processSubmissionFn:
    | ((submission: QueuedSubmission) => Promise<{ jobId: string; assetNumber?: number; isFirstGeneration?: boolean }>)
    | null = null
  let onSuccessFn:
    ((submission: QueuedSubmission, jobId: string, assetNumber?: number, isFirstGeneration?: boolean) => void) | null =
    null

  // Auto-dismiss timer
  let autoDismissInterval: NodeJS.Timeout | null = null

  /**
   * Process the next submission in the queue.
   */
  const processNext = async () => {
    const state = get()
    if (!processorRunning || !processSubmissionFn || !onSuccessFn) {
      set({ isProcessing: false })
      return
    }

    // Find the next queued submission that can be processed
    const submissions = Array.from(state.submissions.values())
    const nextSubmission = submissions.find(s => {
      if (s.status !== 'queued') return false

      // Check dependency
      if (s.dependsOn) {
        const dependency = state.submissions.get(s.dependsOn)
        if (!dependency) {
          // Dependency not found (shouldn't happen, but handle gracefully)
          return true
        }
        if (dependency.status === 'failed') {
          // Dependency failed - mark this as failed too
          get().updateSubmission(s.id, {
            status: 'failed',
            error: {
              code: 'DEPENDENCY_FAILED',
              message: 'Root submission failed',
              retryable: true,
            },
            failedAt: new Date(),
          })

          const draftId = getTrackedDraftAssetId(s.params)
          if (draftId) {
            recordDraftFailureIfIdle(draftId, {
              code: 'DEPENDENCY_FAILED',
              message: 'The generation this one depended on failed',
            })
          }

          return false
        }
        if (dependency.status !== 'completed') {
          // Dependency not yet complete - skip for now
          return false
        }
        // Dependency completed - update parentId with resultJobId
        if (dependency.resultJobId && s.params.preallocatedParentId === null) {
          // This is a variation waiting for its root - update parentId immutably
          const newParentId = dependency.resultJobId // Capture value for type narrowing
          set(state => {
            const existing = state.submissions.get(s.id)
            if (!existing) return state
            const newMap = new Map(state.submissions)
            newMap.set(s.id, {
              ...existing,
              params: { ...existing.params, preallocatedParentId: newParentId },
            })
            return { submissions: newMap }
          })
        }
      }

      return true
    })

    if (!nextSubmission) {
      // No more submissions to process
      set({ isProcessing: false })
      return
    }

    set({ isProcessing: true })

    // Mark as processing
    get().updateSubmission(nextSubmission.id, { status: 'processing' })

    try {
      const result = await processSubmissionFn(nextSubmission)

      // Success - update submission and call success callback
      get().updateSubmission(nextSubmission.id, {
        status: 'completed',
        resultJobId: result.jobId,
      })

      onSuccessFn(nextSubmission, result.jobId, result.assetNumber, result.isFirstGeneration)

      // Remove completed submission from queue
      set(state => {
        const newMap = new Map(state.submissions)
        newMap.delete(nextSubmission.id)
        return { submissions: newMap }
      })
    } catch (err) {
      // Client-side input rejection: the message was written for the user —
      // show it as-is and skip the network-error heuristics entirely.
      if (err instanceof SubmissionInputError) {
        get().updateSubmission(nextSubmission.id, {
          status: 'failed',
          error: { code: 'INVALID_INPUT', message: err.message, retryable: err.retryable },
          failedAt: new Date(),
        })
        const draftId = getTrackedDraftAssetId(nextSubmission.params)
        if (draftId) {
          recordDraftFailureIfIdle(draftId, { code: 'INVALID_INPUT', message: err.message })
        }
        // Same continuation as the generic failure path below.
        setTimeout(() => processNext(), 0)
        return
      }

      // Failure - mark as failed
      const error = err as Error & {
        status?: number
        billingInfo?: { code: string; required?: number; available?: number; billing_mode?: string }
      }
      // HTTP status when the thrower carried one (startGeneration does). Older
      // throwers only embed it in the message, so the string checks stay as a
      // fallback — but status is what a server rejection is actually judged on.
      const status = error.status
      const isInsufficientCredits = error.billingInfo?.code === 'INSUFFICIENT_CREDITS'
      const isSpendCapExceeded = error.billingInfo?.code === 'PROJECT_SPEND_CAP_EXCEEDED'
      const isNonRetryableBilling = isInsufficientCredits || isSpendCapExceeded
      // Every other 402 is a hard billing refusal (domain spend cap, project and
      // member caps, org-level blocks): the server already decided, so retrying
      // re-runs the same check. Insufficient credits is the one exception — it
      // has a shortfall to cover, so it gets the top-up modal instead. Project
      // caps are included deliberately: they open no modal, and the Pixi canvas
      // draws no node, so without this they fail silently.
      const isBillingRefusal = status === 402 && !isInsufficientCredits
      // Non-billing refusals the server makes on purpose, whose `error` is copy
      // written for the user. Same reasoning as above: nothing else draws them,
      // and they are decisions rather than faults, so they don't belong in Sentry.
      const isCodedRefusal =
        status !== undefined && status < 500 && INTENTIONAL_REFUSAL_CODES.has((error as { code?: string }).code ?? '')
      const isExpectedRefusal = isBillingRefusal || isCodedRefusal
      // Another generation (e.g. MCP/agent) already holds the target vessel's
      // lock — the backend rejected this submission with 409.
      const isGenerationInProgress = (error as { code?: string }).code === 'GENERATION_IN_PROGRESS'
      const is429 = status === 429 || error.message?.includes('429')
      const is4xx =
        (status !== undefined && status >= 400 && status < 500 && status !== 429) ||
        (error as { code?: string }).code === 'FILE_TOO_LARGE' ||
        error.message?.includes('400') ||
        error.message?.includes('402') ||
        error.message?.includes('403')

      // A response arrived with a 5xx: the server answered, so this is a fault
      // on our side, not the user's connection. Only a missing status (the
      // transport threw before any response) is a network problem.
      const isServerFault = status !== undefined && status >= 500
      const errorCode = isNonRetryableBilling
        ? '402'
        : isGenerationInProgress
          ? '409'
          : is429
            ? '429'
            : is4xx
              ? '4xx'
              : isServerFault
                ? 'SERVER_ERROR'
                : 'NETWORK_ERROR'

      // Server-authored transient failure (storage/CDN read blip, returned 503
      // with copy written for the user). Unlike a generic 5xx, its message is
      // NOT diagnostics — overwriting it with a generic fallback would hide
      // the one sentence that tells the user what to do.
      const isServerTransient = (error as { code?: string }).code === 'INPUT_FETCH_FAILED'
      // A 5xx `message` is diagnostics written for Sentry, never copy for the
      // UI (one field, one audience), so both fallbacks below are ours. Neither
      // may assert a cause we don't know: the connection copy only fires when
      // no response came back at all.
      const userMessage =
        errorCode === 'NETWORK_ERROR'
          ? 'Connection failed — please check your network and retry'
          : errorCode === 'SERVER_ERROR' && !isServerTransient
            ? "Generation couldn't start. Try again in a moment; if it keeps failing, contact support."
            : error.message || 'Submission failed'

      get().updateSubmission(nextSubmission.id, {
        status: 'failed',
        error: {
          code: errorCode,
          message: userMessage,
          retryable: !isNonRetryableBilling && !isGenerationInProgress && (is429 || !is4xx),
          ...(isNonRetryableBilling && {
            requiredCost: error.billingInfo?.required,
            currentBalance: error.billingInfo?.available,
            billingMode: error.billingInfo?.billing_mode,
          }),
        },
        failedAt: new Date(),
      })

      // Auto-open topup modal on insufficient credits
      if (isInsufficientCredits) {
        useInsufficientCreditsStore
          .getState()
          .trigger(error.billingInfo?.required ?? 0, error.billingInfo?.available ?? 0, error.billingInfo?.billing_mode)
      }

      // Refusals have no node-level surface on the Pixi canvas (submission
      // placeholders are local-only, so nothing is drawn), and topping up won't
      // clear them. Show the server's explanation in a dialog instead.
      if (isExpectedRefusal) {
        useGenerationBlockedStore
          .getState()
          .trigger(userMessage, error.billingInfo?.code ?? (error as { code?: string }).code, {
            canUpgrade: (error as { canUpgrade?: boolean }).canUpgrade,
          })
      }

      // Report to Sentry (skip expected billing/lock-conflict errors)
      if (!isNonRetryableBilling && !isExpectedRefusal && !isGenerationInProgress) {
        captureSentryError(error, {
          operation: 'submission_processor',
          level: errorCode === 'NETWORK_ERROR' ? 'warning' : 'error',
          projectId: nextSubmission.params.projectId,
          tags: {
            error_code: errorCode,
            model: nextSubmission.params.generationParams.modelId,
            ...(status !== undefined ? { http_status: String(status) } : {}),
            ...((error as { code?: string }).code ? { server_code: (error as { code?: string }).code } : {}),
          },
          extra: {
            submissionId: nextSubmission.id,
            sceneId: nextSubmission.params.sceneId,
          },
        })
      }

      // Clear the Yjs generatingStartedAt so collaborators don't see a stuck
      // generating state. Skip on GENERATION_IN_PROGRESS: the lock belongs to
      // the other, still-running generation — clearing it would unlock a
      // vessel that is legitimately generating.
      const draftId = getTrackedDraftAssetId(nextSubmission.params)
      if (draftId && !isGenerationInProgress) {
        // Every failure class gets the node-level chip — dialogs (billing
        // refusals, top-up) are transient, and the other classes previously
        // reverted the draft with no trace at all.
        recordDraftFailureIfIdle(draftId, { code: errorCode, message: userMessage })
      }
    }

    // Continue processing (next tick to avoid stack overflow)
    setTimeout(() => processNext(), 0)
  }

  /**
   * Check and auto-dismiss stale failed submissions.
   */
  const checkAutoDismiss = () => {
    const now = Date.now()
    const state = get()
    const toRemove: string[] = []

    for (const [id, submission] of state.submissions) {
      if (
        submission.status === 'failed' &&
        submission.failedAt &&
        now - submission.failedAt.getTime() > AUTO_DISMISS_MS
      ) {
        toRemove.push(id)
      }
    }

    if (toRemove.length > 0) {
      const draftIds = new Set<string>()
      const currentState = get()
      for (const id of toRemove) {
        const submission = currentState.submissions.get(id)
        const draftId = submission ? getTrackedDraftAssetId(submission.params) : undefined
        if (draftId) {
          draftIds.add(draftId)
        }
      }

      set(state => {
        const newMap = new Map(state.submissions)
        for (const id of toRemove) {
          newMap.delete(id)
        }
        return { submissions: newMap }
      })

      for (const draftId of draftIds) {
        syncDraftGeneratingState(draftId)
      }
    }
  }

  return {
    submissions: new Map(),
    isProcessing: false,
    recentSubmissionTimestamps: [],
    showFirstGenerationModal: false,
    firstGenerationContext: null,
    firstGenerationCompleted: false,
    showOnboardingAnimaticModal: false,
    onboardingAnimaticContext: null,
    pendingContextMenuNodeId: null,

    enqueue: (params, dependsOn) => {
      const id = uuid()
      const submission: QueuedSubmission = {
        id,
        params,
        dependsOn,
        status: 'queued',
        createdAt: new Date(),
        parentPositionAtSubmission: captureParentPositionAtSubmission(params.preallocatedParentId),
      }

      set(state => {
        const newMap = new Map(state.submissions)
        newMap.set(id, submission)
        return {
          submissions: newMap,
          recentSubmissionTimestamps: [...state.recentSubmissionTimestamps, new Date()],
        }
      })

      // Arm the draft's generating state and clear any previous attempt's
      // failure chip here, at the boundary every submission passes through —
      // the panels write generatingStartedAt themselves but know nothing
      // about generationFailure, which would otherwise linger in the shared
      // doc until a retry() (never, for an ordinary re-submit).
      const enqueuedDraftId = getTrackedDraftAssetId(params)
      if (enqueuedDraftId) {
        setDraftGeneratingState(enqueuedDraftId, true)
      }

      // Trigger processor if not already running
      if (processorRunning && !get().isProcessing) {
        processNext()
      }

      return id
    },

    enqueueBatch: batch => {
      const ids: string[] = []
      const now = new Date()

      set(state => {
        const newMap = new Map(state.submissions)
        const newTimestamps = [...state.recentSubmissionTimestamps]

        for (const { params, dependsOn } of batch) {
          const id = uuid()
          ids.push(id)

          const submission: QueuedSubmission = {
            id,
            params,
            dependsOn,
            status: 'queued',
            createdAt: now,
            parentPositionAtSubmission: captureParentPositionAtSubmission(params.preallocatedParentId),
          }

          newMap.set(id, submission)
          newTimestamps.push(now)
        }

        return {
          submissions: newMap,
          recentSubmissionTimestamps: newTimestamps,
        }
      })

      // Same arming/clearing as enqueue() — one write per distinct draft.
      const batchDraftIds = new Set<string>()
      for (const { params } of batch) {
        const draftId = getTrackedDraftAssetId(params)
        if (draftId) batchDraftIds.add(draftId)
      }
      for (const draftId of batchDraftIds) {
        setDraftGeneratingState(draftId, true)
      }

      // Trigger processor if not already running
      if (processorRunning && !get().isProcessing) {
        processNext()
      }

      return ids
    },

    retry: submissionId => {
      const submission = get().submissions.get(submissionId)
      if (!submission || submission.status !== 'failed') return

      // Re-enqueue at the back with fresh status
      set(state => {
        const newMap = new Map(state.submissions)
        newMap.delete(submissionId)
        newMap.set(submissionId, {
          ...submission,
          status: 'queued',
          error: undefined,
          failedAt: undefined,
          // Clear dependency - retry should work independently
          dependsOn: undefined,
        })
        return { submissions: newMap }
      })

      const draftId = getTrackedDraftAssetId(submission.params)
      if (draftId) {
        setDraftGeneratingState(draftId, true)
      }

      // Trigger processor if not already running
      if (processorRunning && !get().isProcessing) {
        processNext()
      }
    },

    dismiss: submissionId => {
      const submission = get().submissions.get(submissionId)
      const draftId = submission ? getTrackedDraftAssetId(submission.params) : undefined

      set(state => {
        const newMap = new Map(state.submissions)
        newMap.delete(submissionId)
        return { submissions: newMap }
      })

      if (draftId) {
        syncDraftGeneratingState(draftId)
      }
    },

    updateSubmission: (submissionId, updates) => {
      set(state => {
        const existing = state.submissions.get(submissionId)
        if (!existing) return state

        const newMap = new Map(state.submissions)
        newMap.set(submissionId, { ...existing, ...updates })
        return { submissions: newMap }
      })
    },

    getSubmission: submissionId => get().submissions.get(submissionId),

    getSubmissionsForScene: sceneId => {
      return Array.from(get().submissions.values()).filter(
        s => s.params.sceneId === sceneId && s.status !== 'completed',
      )
    },

    getPlaceholdersForScene: sceneId => {
      return get().getSubmissionsForScene(sceneId).map(submissionToPlaceholder)
    },

    clear: () => {
      const draftIds = new Set<string>()
      for (const submission of get().submissions.values()) {
        const draftId = getTrackedDraftAssetId(submission.params)
        if (draftId) {
          draftIds.add(draftId)
        }
      }

      for (const draftId of draftIds) {
        setDraftGeneratingState(draftId, false)
      }

      set({ submissions: new Map(), recentSubmissionTimestamps: [] })
    },

    getQueueDepth: () => {
      return Array.from(get().submissions.values()).filter(s => s.status === 'queued' || s.status === 'processing')
        .length
    },

    checkRateLimit: () => {
      const now = Date.now()
      const windowStart = now - RATE_LIMIT_WINDOW_MS

      // Filter to recent timestamps (read-only, no state mutation)
      const recentTimestamps = get().recentSubmissionTimestamps.filter(t => t.getTime() > windowStart)
      const count = recentTimestamps.length

      // Schedule cleanup of expired timestamps outside of render (functional update to avoid stale closures)
      if (recentTimestamps.length !== get().recentSubmissionTimestamps.length) {
        queueMicrotask(() =>
          set(state => {
            const cutoff = Date.now() - RATE_LIMIT_WINDOW_MS
            const filtered = state.recentSubmissionTimestamps.filter(t => t.getTime() > cutoff)
            return filtered.length === state.recentSubmissionTimestamps.length
              ? state
              : { recentSubmissionTimestamps: filtered }
          }),
        )
      }

      return {
        limited: count >= HARD_LIMIT_COUNT,
        softWarning: count >= SOFT_LIMIT_COUNT,
      }
    },

    startProcessor: (processSubmission, onSuccess) => {
      if (processorRunning) return

      processorRunning = true
      processSubmissionFn = processSubmission
      onSuccessFn = onSuccess

      // Start auto-dismiss timer
      autoDismissInterval = setInterval(checkAutoDismiss, 30_000) // Check every 30s

      // Start processing if there are pending submissions
      if (!get().isProcessing) {
        processNext()
      }
    },

    stopProcessor: () => {
      processorRunning = false
      processSubmissionFn = null
      onSuccessFn = null

      if (autoDismissInterval) {
        clearInterval(autoDismissInterval)
        autoDismissInterval = null
      }
    },

    updateSubmissionPosition: (submissionId, position) => {
      set(state => {
        const existing = state.submissions.get(submissionId)
        if (!existing) return state

        const newMap = new Map(state.submissions)
        newMap.set(submissionId, {
          ...existing,
          params: { ...existing.params, preallocatedPosition: position },
        })
        return { submissions: newMap }
      })
    },

    setProcessing: isProcessing => {
      set({ isProcessing })
    },

    dismissFirstGenerationModal: () => {
      const { firstGenerationCompleted } = get()
      if (firstGenerationCompleted) {
        // Completed phase dismissed — fully clear all state
        set({ showFirstGenerationModal: false, firstGenerationContext: null, firstGenerationCompleted: false })
      } else {
        // Generating phase dismissed — keep context so the WebSocket completion
        // handler can still match the jobId and reopen with the tutorial
        set({ showFirstGenerationModal: false })
      }
    },

    setFirstGenerationCompleted: () => {
      set({ firstGenerationCompleted: true })
    },

    /**
     * Called when a generation job completes. If it matches the first generation
     * context and is a video, transitions the modal to the completed/tutorial phase.
     * Safe to call from any completion path (WebSocket, polling, JobStatusPoller).
     */
    checkFirstGenerationCompleted: (completedJobId: string) => {
      const { firstGenerationContext, showFirstGenerationModal, firstGenerationCompleted } = get()
      if (firstGenerationCompleted) return
      if (!firstGenerationContext || firstGenerationContext.jobId !== completedJobId) return
      if (
        firstGenerationContext.operation === 'generate-image' ||
        firstGenerationContext.operation === 'generate-audio'
      )
        return

      set({
        firstGenerationCompleted: true,
        ...(!showFirstGenerationModal ? { showFirstGenerationModal: true } : {}),
      })
    },

    showOnboardingAnimaticModalForJob: context => {
      set({ showOnboardingAnimaticModal: true, onboardingAnimaticContext: context })
    },

    dismissOnboardingAnimaticModal: () => {
      set({ showOnboardingAnimaticModal: false })
    },

    setPendingContextMenuNodeId: (nodeId: string | null) => {
      set({ pendingContextMenuNodeId: nodeId })
    },
  }
})
